import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import API from "../../Network/API";
import ShareMeeting from "../Modal/ShareMeeting";
import RecordingPlayerModal from "../Modal/RecordingPlayerModal";
import { toast } from 'react-toastify';
import DOMPurify from 'dompurify';

export default function MeetingDetails() {
  const myArr = JSON.parse(localStorage.getItem('user_data'));
  const [meetingData, setMeetingData] = useState([]);
  const [progress, setProgress] = useState(false);
  let { room } = useParams();
  const [modalDialog, setModalDialog] = useState(false);
  const [sc_meet, setScMeet] = useState(null);
  const [recordingModal, setRecordingModal] = useState(false);
  // RSVP state — seeded from API, updated optimistically on user action
  const [rsvpStatus, setRsvpStatus] = useState(null); // 1=accepted 2=pending 3=declined
  const [rsvpLoading, setRsvpLoading] = useState(false);

  const RSVP_LABELS = { 1: 'Accepted', 2: 'Maybe', 3: 'Declined' };
  const RSVP_COLORS = { 1: '#22c55e', 2: '#f59e0b', 3: '#ef4444' };
  const RSVP_ICONS  = { 1: 'fa-check', 2: 'fa-question', 3: 'fa-times' };

  const handleRsvp = async (status) => {
    const statusMap = { 1: 'accept', 2: 'maybe', 3: 'reject' };
    setRsvpLoading(true);
    const prev = rsvpStatus;
    setRsvpStatus(status); // optimistic
    try {
      await API.post('/self-meeting-status-modify', {
        meeting_room_id: meetingData?.id,
        status: statusMap[status],
      });
    } catch (e) {
      setRsvpStatus(prev); // rollback on failure
      toast.error('Could not update RSVP. Please try again.');
    } finally {
      setRsvpLoading(false);
    }
  };

  const toggleModal = (meeting_room) => {
    setScMeet(meeting_room);
    setModalDialog(!modalDialog);
  };

  useEffect(() => {

    setProgress(true)
    API.get('/v1/meeting_details/' + room).then((response) => {
      const d = response.data.data;
      setMeetingData(d);
      if (d?.rsvp_status) setRsvpStatus(d.rsvp_status);
      setProgress(false)
    }).catch((error) => {
      setProgress(true)
    });

  }, [room]);

  return (
  <>
      {progress ? (
        <div className="min-vh-100 bg-white d-flex align-items-center justify-content-center">
          <div className="text-center">
            <div className="spinner-border text-dark mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">Loading meeting details...</p>
          </div>
        </div>
      ) : (
        <div className="min-vh-100" style={{ backgroundColor: '#fcfcfc' }}>
          <div className="container py-5">
            <div className="row justify-content-center">
              <div className="col-12 col-lg-9 col-xl-8 text-start">
                
                {/* 1. Back Navigation */}
                <Link
                  to="/meeting"
                  className="btn btn-light rounded-pill px-4 py-2 d-inline-flex align-items-center mb-4 shadow-sm border border-light"
                  style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}
                >
                  <i className="fa-solid fa-arrow-left me-2" style={{ fontSize: '11px' }}></i>
                  Back
                </Link>

                {/* 2. Header & Primary Action Row */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-5 gap-4">
                  <div>
                    <h1 className="fw-bolder text-dark mb-3" style={{ fontSize: '32px', letterSpacing: '-0.5px' }}>
                      {meetingData?.room_title}
                    </h1>
                    
                    {/* Inline Date / Time Badges */}
                    <div className="d-flex flex-wrap gap-2 text-muted" style={{ fontSize: '14px', fontWeight: '500' }}>
                      <div className="d-flex align-items-center bg-white border rounded-pill px-3 py-2 shadow-sm">
                         <i className="fa-solid fa-calendar me-2 text-dark"></i>
                         {meetingData?.start_text}
                      </div>
                      <div className="d-flex align-items-center bg-white border rounded-pill px-3 py-2 shadow-sm">
                         <i className="fa-solid fa-clock me-2 text-dark"></i>
                         {meetingData?.start_time_text}
                      </div>
                    </div>
                  </div>

                  {/* Primary Action Button (Moved UP!) */}
                  <div className="w-100 w-md-auto">
                    <Link 
                      className="btn btn-dark rounded-pill px-4 py-3 shadow-sm d-inline-flex align-items-center justify-content-center w-100" 
                      to={`/meeting/${meetingData?.room_name}`} 
                      style={{ fontSize: '15px', fontWeight: '600', transition: 'all 0.2s ease', minWidth: '180px' }}
                    >
                      <i className="fa-solid fa-video me-2" style={{ fontSize: '14px' }}></i>
                      {meetingData?.user_id === myArr?.id ? 'Start Meeting' : 'Join Meeting'}
                    </Link>
                  </div>
                </div>

                <hr className="my-5 opacity-10" />

                {/* 3. Host Profile & RSVP block */}
                {meetingData?.user_id === myArr?.id ? (
                  <div className="mb-5">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h6 className="fw-semibold text-dark mb-0" style={{ fontSize: '16px' }}>
                        Participants
                      </h6>
                      <button
                        className="btn btn-light rounded-pill px-3 py-1 shadow-sm d-flex align-items-center border"
                        onClick={() => toggleModal(meetingData)}
                        style={{ fontSize: '12px', fontWeight: '600' }}
                      >
                        <i className="fa-solid fa-plus me-2 text-muted"></i> Add
                      </button>
                    </div>
                    
                    <div className="d-flex align-items-start flex-wrap gap-3 p-4 bg-white rounded-4 border shadow-sm">
                      {meetingData?.total_invites > 0 ? meetingData?.users.map((user, index) => (
                        <Link key={index} to={`/contact/${user.id}`} className="text-decoration-none">
                          <div className="d-flex flex-column align-items-center" style={{ maxWidth: '64px' }}>
                            <div 
                              className="rounded-circle shadow-sm mb-2" 
                              style={{ 
                                width: '48px', height: '48px',
                                backgroundImage: `url(${user.avatar})`, backgroundSize: 'cover', backgroundPosition: 'center',
                                border: `2px solid ${RSVP_COLORS[user.rsvp_status] ?? '#e9ecef'}`,
                              }} 
                            />
                            {user.rsvp_status && (
                              <span className="badge rounded-pill mb-1 shadow-sm" style={{
                                fontSize: '8px', fontWeight: '600',
                                backgroundColor: `${RSVP_COLORS[user.rsvp_status]}15`, color: RSVP_COLORS[user.rsvp_status],
                                border: `1px solid ${RSVP_COLORS[user.rsvp_status]}30`, padding: '2px 6px',
                              }}>
                                <i className={`fa-solid ${RSVP_ICONS[user.rsvp_status]} me-1`} style={{ fontSize: '7px' }} />
                                {RSVP_LABELS[user.rsvp_status]}
                              </span>
                            )}
                            <span className="text-dark text-center fw-medium" style={{ fontSize: '11px', lineHeight: '1.2', WebkitLineClamp: 2, display: '-webkit-box', WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {user.name}
                            </span>
                          </div>
                        </Link>
                      )) : <span className="text-muted" style={{ fontSize: '14px' }}>No participants invited yet.</span>}
                    </div>
                  </div>
                ) : (
                  <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-5 gap-4 p-4 bg-white rounded-4 border shadow-sm">
                    
                    {/* Host Identity Pill */}
                    <Link to={`/contact/${meetingData?.user_id}`} className="text-decoration-none">
                      <div className="d-flex align-items-center">
                        <div 
                          className="rounded-circle me-3 shadow-sm" 
                          style={{ 
                            width: '48px', height: '48px',
                            backgroundImage: `url(${meetingData?.avatar})`, backgroundSize: 'cover', backgroundPosition: 'center',
                          }} 
                        />
                        <div>
                          <div className="fw-bold text-dark" style={{ fontSize: '15px' }}>{meetingData?.created_by}</div>
                          <div className="text-secondary" style={{ fontSize: '12px', fontWeight: '500' }}>Meeting Host</div>
                        </div>
                      </div>
                    </Link>

                    {/* Segmented RSVP Control */}
                    <div className="d-flex align-items-center bg-light rounded-pill p-1 border">
                      {[1, 2, 3].map((s) => (
                        <button
                          key={s}
                          disabled={rsvpLoading}
                          onClick={() => handleRsvp(s)}
                          className={`btn rounded-pill border-0 px-3 py-2 ${rsvpStatus === s ? 'shadow-sm bg-white' : ''}`}
                          style={{
                            fontSize: '13px', fontWeight: '600',
                            color: rsvpStatus === s ? RSVP_COLORS[s] : '#64748b',
                            transition: 'all 0.25s',
                          }}
                        >
                          <i className={`fa-solid ${RSVP_ICONS[s]} me-2`} style={{ fontSize: '11px' }} />
                          {RSVP_LABELS[s]}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* 4. The Agenda Card */}
                {meetingData?.note && (
                  <div className="mb-5">
                    <h6 className="fw-semibold text-dark mb-3" style={{ fontSize: '16px' }}>
                      Agenda & Notes
                    </h6>
                    <div 
                      className="text-start rounded-4 p-4 p-md-5" 
                      style={{ 
                        backgroundColor: '#ffffff',
                        color: '#334155',
                        fontSize: '15px', 
                        lineHeight: '1.8', 
                        whiteSpace: 'pre-wrap',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                      }} 
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(meetingData.note) }}
                    />
                  </div>
                )}

                {/* 5. Recording Section */}
                {meetingData?.is_recorded && (
                  <div className="mb-5">
                    <h6 className="fw-semibold text-dark mb-3" style={{ fontSize: '16px' }}>
                      Meeting Recording
                    </h6>
                    <div style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #2d1515 100%)', borderRadius: '16px', padding: '20px', border: '1px solid rgba(239,68,68,0.2)' }}>
                      <div onClick={() => setRecordingModal(true)} style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', background: '#000', height: '140px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #0d0d0d 0%, #1a0a0a 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                          <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(239,68,68,0.15)', border: '2px solid rgba(239,68,68,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>
                            <i className="fa-solid fa-play" style={{ fontSize: '20px', color: '#ef4444', marginLeft: '4px' }} />
                          </div>
                        </div>
                        <span style={{ position: 'absolute', top: '10px', left: '10px', background: '#ef4444', color: '#fff', fontSize: '9px', fontWeight: '700', letterSpacing: '0.8px', padding: '3px 8px', borderRadius: '4px' }}>● REC</span>
                      </div>
                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div className="d-flex gap-3 flex-wrap">
                          {meetingData?.recording_ended_at && (
                            <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '11px' }}>📅 {new Date(meetingData.recording_ended_at).toLocaleDateString()}</span>
                          )}
                          {meetingData?.recording_size_bytes && (
                            <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '11px' }}>
                              💾 {meetingData.recording_size_bytes >= 1048576 ? `${(meetingData.recording_size_bytes / 1048576).toFixed(1)} MB` : `${(meetingData.recording_size_bytes / 1024).toFixed(1)} KB`}
                            </span>
                          )}
                        </div>
                        <button onClick={() => setRecordingModal(true)} style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <i className="fa-solid fa-circle-play" style={{ fontSize: '12px' }} /> Watch Recording
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
              </div>
            </div>
          </div>
        </div>
      )}

    <ShareMeeting isModalVisible={modalDialog} meetingRoom={sc_meet} handleShowB={() => setModalDialog(false)}/>

    {/* ── Recording Player Modal ── */}
    <RecordingPlayerModal
      isOpen={recordingModal}
      onClose={() => setRecordingModal(false)}
      livekitRoom={meetingData?.livekit_room_name}
      meetingTitle={meetingData?.room_title}
      endedAt={meetingData?.recording_ended_at}
    />
    </>
  )
}